import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';

/** Only ship sections with real content in the hub (AdSense thin-content mitigation). */
const HUB_CARDS = [
  {
    to: '/islam/articles',
    icon: '📰',
    title: 'Articles',
    description: 'Islamic guides, education, and community resources published on PakPoints',
    orb: 'from-cyan-400/40 to-blue-600/20',
    border: 'group-hover:border-cyan-400/50',
  },
  {
    to: '/islam/mosques',
    icon: '🕌',
    title: 'Mosques',
    description: 'Explore mosques in your societies—locations, timings, and community details on PakPoints',
    orb: 'from-sky-400/40 to-blue-600/20',
    border: 'group-hover:border-sky-400/50',
  },
  {
    to: '/islam/education',
    icon: '🎓',
    title: 'Education',
    description: 'Educational articles and learning pieces published on PakPoints',
    orb: 'from-indigo-400/40 to-blue-700/20',
    border: 'group-hover:border-indigo-400/50',
  },
];

/**
 * Purpose: Islam hub—entry to published resources (articles, mosques, education).
 * Inputs: none (static marketing / navigation surface).
 * Outputs: Routed page with tappable cards linking to ready subsections.
 */
const IslamPage = () => {
  return (
    <PageLayout title="Islam - PakPoints">
      <Helmet>
        <title>Islam - PakPoints</title>
        <meta
          name="description"
          content="Read Islamic articles and find mosques on PakPoints—practical guides and community resources for Muslims in Pakistan."
        />
      </Helmet>
      <div className="relative min-h-[calc(100vh-10rem)] overflow-hidden bg-slate-950 text-slate-100">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 100% 70% at 50% -25%, rgba(129, 140, 248, 0.45), transparent 50%), radial-gradient(ellipse 70% 50% at 100% 0%, rgba(34, 211, 238, 0.12), transparent 45%)',
          }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute left-1/2 top-1/3 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-fuchsia-600/10 blur-3xl"
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
          <header className="mb-12 text-center">
            <div className="mb-6 text-6xl sm:text-7xl" aria-hidden>
              🕌
            </div>
            <h1 className="bg-gradient-to-r from-white via-cyan-100 to-violet-200 bg-clip-text text-4xl font-extrabold tracking-tight text-transparent sm:text-5xl">
              Islam
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300 sm:text-xl">
              Read original Islamic articles, explore mosques in residential societies, and find educational
              resources published on PakPoints.
            </p>
          </header>

          <div className="rounded-3xl border border-white/10 bg-white/[0.05] p-6 shadow-[0_0_80px_-20px_rgba(99,102,241,0.35)] backdrop-blur-2xl sm:p-10">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {HUB_CARDS.map((card) => (
                <Link
                  key={card.to}
                  to={card.to}
                  className={`group relative block overflow-hidden rounded-2xl border border-white/10 bg-slate-900/40 p-6 text-center no-underline text-inherit shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-cyan-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${card.border}`}
                >
                  <div
                    className={`pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gradient-to-br opacity-60 blur-2xl transition group-hover:opacity-100 ${card.orb}`}
                    aria-hidden
                  />
                  <div className="relative">
                    <div className="mb-3 text-4xl" aria-hidden>
                      {card.icon}
                    </div>
                    <h3 className="text-lg font-bold text-white">{card.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400 group-hover:text-slate-200">
                      {card.description}
                    </p>
                    <span className="mt-4 inline-flex items-center text-xs font-semibold uppercase tracking-wider text-cyan-300/90">
                      Enter
                      <span className="ml-1 transition group-hover:translate-x-1" aria-hidden>
                        →
                      </span>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default IslamPage;
